import { useEffect, useRef, useState } from 'react'
import { useIsFetching } from '@tanstack/react-query'

/**
 * Tracks the on-screen box of a walkthrough anchor, and keeps tracking it.
 *
 * The naive version of this — scroll the element into view, wait a fixed 300ms,
 * measure once — is wrong in three ways that all show up as a highlight sitting
 * next to the thing it is supposed to be highlighting:
 *
 *   1. `scrollIntoView({behavior:'smooth'})` usually takes longer than the
 *      guessed delay, so the single measurement catches the page mid-scroll.
 *   2. Closing the assistant panel animates the content column's width for
 *      400ms, so the anchor is still moving after the scroll has finished.
 *   3. Nothing re-measures afterwards, so the first scroll or resize by the
 *      user detaches the highlight entirely.
 *
 * So: measure immediately, keep measuring on scroll/resize/element-resize for
 * as long as the anchor is live, and additionally re-measure once the scroll
 * genuinely settles (`scrollend`, with a timeout as the fallback rather than as
 * the plan). The spotlight has a CSS transition, so the intermediate
 * measurements read as it gliding into place.
 */

/** How long to hunt for an anchor while the app is idle. */
const IDLE_FIND_MS = 1500
/**
 * Absolute ceiling on the hunt, however busy the app stays.
 *
 * Needed because a prep screen polls its report every 3s while the analysis is
 * running, so "something is fetching" is not a rare state there — without a
 * ceiling a genuinely absent anchor would search forever.
 *
 * 29 seconds because that is API Gateway's integration timeout: past it, no
 * in-flight request can still be alive, so anything the anchor was waiting on
 * has already failed. Waiting longer cannot help.
 */
const MAX_FIND_MS = 29_000
const SETTLE_FALLBACK_MS = 550

export type SpotlightRect = {
  top: number
  left: number
  width: number
  height: number
}

/**
 * `searching` is distinct from `missing` on purpose: a page that is still
 * fetching has not failed to show the anchor, it just has not got there yet, and
 * telling the user to skip ahead would be wrong.
 */
export type SpotlightStatus = 'searching' | 'found' | 'missing'

export type SpotlightResult = {
  rect: SpotlightRect | null
  status: SpotlightStatus
  /** True once we have given up looking for the anchor. */
  missing: boolean
  /** The spotlight has moved onto an open dialog rather than the step's anchor. */
  onModal: boolean
}

/** How often the target is re-checked once found, to notice a dialog opening. */
const RETARGET_MS = 200

/**
 * An open antd dialog, if there is one.
 *
 * A dialog takes over: it is the only thing the user can act on, and — now that
 * the dim blocks clicks — anything outside the spotlight is inert. So a dialog
 * opening mid-walkthrough must become the spotlight, or the walkthrough would
 * lock the user out of the very thing it just told them to open.
 *
 * Deliberately generic rather than a `data-tour` on each dialog: there are four
 * in the prep flow alone, and the failure mode for a missed one is being unable
 * to continue.
 */
/**
 * The dialog card, across antd versions.
 *
 * antd 6 renders the panel as `.ant-modal` with `.ant-modal-container` inside;
 * the `.ant-modal-content` of antd 4 and 5 is gone. Both are listed so an
 * upgrade in either direction does not silently break the spotlight — the
 * failure is invisible, because a missed dialog just means the highlight sits
 * on the page behind it.
 */
const MODAL_PANEL_SELECTORS = ['.ant-modal-content', '.ant-modal']

function findOpenModal(): Element | null {
  // The wrapper carries an inline `display: none` while the dialog is closed,
  // and closed dialogs stay in the DOM once they have been opened.
  const wraps = document.querySelectorAll('.ant-modal-wrap')
  for (const wrap of wraps) {
    if (!(wrap instanceof HTMLElement) || wrap.style.display === 'none') continue
    for (const selector of MODAL_PANEL_SELECTORS) {
      const panel = wrap.querySelector(selector)
      if (!panel) continue
      const rect = panel.getBoundingClientRect()
      if (rect.width > 0 && rect.height > 0) return panel
    }
  }
  return null
}

export const anchorSelector = (anchor: string) => `[data-tour="${CSS.escape(anchor)}"]`

/**
 * The first *visible* element carrying this anchor.
 *
 * `querySelector` is not enough, for two reasons that both bite in the sidebar:
 *
 *  - The nav is rendered twice — once for desktop, once as a mobile drawer — so
 *    two elements share the anchor and the first in DOM order is `display:none`
 *    on narrow viewports.
 *  - A collapsed sidebar keeps its links in the DOM inside a zero-width
 *    container, so an element can exist and still have no box to point at.
 *
 * Returning null in both cases lets the caller keep retrying, which is what
 * gives the layout time to expand the sidebar before we measure it.
 */
function findVisibleAnchor(anchor: string): Element | null {
  const matches = document.querySelectorAll(anchorSelector(anchor))
  for (const element of matches) {
    const rect = element.getBoundingClientRect()
    if (rect.width > 0 && rect.height > 0) return element
  }
  return null
}

const rectsMatch = (a: SpotlightRect[], b: SpotlightRect[]) =>
  a.length === b.length &&
  a.every((rect, index) => {
    const other = b[index]
    return (
      other !== undefined &&
      Math.round(rect.top) === Math.round(other.top) &&
      Math.round(rect.left) === Math.round(other.left) &&
      Math.round(rect.width) === Math.round(other.width) &&
      Math.round(rect.height) === Math.round(other.height)
    )
  })

/** Every visible element carrying the anchor, for a step that highlights a repeated control. */
export function useAnchorRects(anchor: string | null, enabled: boolean): SpotlightRect[] {
  const [rects, setRects] = useState<SpotlightRect[]>([])

  useEffect(() => {
    if (!anchor || !enabled) {
      setRects([])
      return
    }

    let disposed = false
    let frame = 0

    const measure = () => {
      if (disposed) return
      const next: SpotlightRect[] = []
      document.querySelectorAll(anchorSelector(anchor)).forEach((element) => {
        const r = element.getBoundingClientRect()
        if (r.width > 0 && r.height > 0) {
          next.push({ top: r.top, left: r.left, width: r.width, height: r.height })
        }
      })
      // Rounded, or scrolling re-renders the overlay on sub-pixel differences.
      setRects((prev) => (rectsMatch(prev, next) ? prev : next))
    }

    const schedule = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        measure()
      })
    }

    measure()
    window.addEventListener('scroll', schedule, true)
    window.addEventListener('resize', schedule)
    const poll = window.setInterval(measure, RETARGET_MS)

    return () => {
      disposed = true
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule, true)
      window.removeEventListener('resize', schedule)
      window.clearInterval(poll)
    }
  }, [anchor, enabled])

  return rects
}

export function useSpotlight(anchor: string | null, focusAnchor?: string): SpotlightResult {
  const [rect, setRect] = useState<SpotlightRect | null>(null)
  const [status, setStatus] = useState<SpotlightStatus>('searching')
  const [onModal, setOnModal] = useState(false)

  /**
   * How many queries are in flight, read through a ref.
   *
   * Deliberately not a dependency of the effect below: the count changes every
   * time any query starts or finishes, and restarting the search on each of
   * those would reset the very deadline it is meant to extend.
   */
  const isFetching = useIsFetching()
  const isFetchingRef = useRef(isFetching)
  isFetchingRef.current = isFetching

  useEffect(() => {
    setRect(null)
    setStatus('searching')
    setOnModal(false)
    if (!anchor) return

    let disposed = false
    let frame = 0
    let findFrame = 0
    let retargetTimer = 0
    let current: Element | null = null
    let detach: (() => void) | null = null

    const measure = (element: Element) => {
      if (disposed) return
      // A detached node measures as a zero box at the origin; keep the last one
      // until `retarget` finds its replacement.
      if (!element.isConnected) return
      const r = element.getBoundingClientRect()
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height })
    }

    /** Follow one element, returning the cleanup for it. */
    const attach = (element: Element): (() => void) => {
      let settled = false
      let settleTimer = 0

      const schedule = () => {
        if (frame) return
        frame = requestAnimationFrame(() => {
          frame = 0
          measure(element)
        })
      }

      measure(element)
      setStatus('found')

      // Capture phase so scrolling inside any container counts, not just window.
      window.addEventListener('scroll', schedule, true)
      window.addEventListener('resize', schedule)
      const observer = new ResizeObserver(schedule)
      observer.observe(element)

      const onSettled = () => {
        if (settled || disposed) return
        settled = true
        window.removeEventListener('scrollend', onSettled)
        window.clearTimeout(settleTimer)
        measure(element)
      }
      window.addEventListener('scrollend', onSettled)
      settleTimer = window.setTimeout(onSettled, SETTLE_FALLBACK_MS)

      // A dialog is already centred and owns the screen; scrolling to it would
      // only jerk the page behind it about.
      if (!element.closest('.ant-modal-wrap')) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }

      return () => {
        window.removeEventListener('scroll', schedule, true)
        window.removeEventListener('resize', schedule)
        window.removeEventListener('scrollend', onSettled)
        window.clearTimeout(settleTimer)
        observer.disconnect()
      }
    }

    /** A dialog wins, then the step's narrower subject if present, then its anchor. */
    const resolveTarget = () =>
      findOpenModal() ??
      (focusAnchor ? findVisibleAnchor(focusAnchor) : null) ??
      findVisibleAnchor(anchor)

    const follow = (element: Element) => {
      detach?.()
      current = element
      setOnModal(Boolean(element.closest('.ant-modal-wrap')))
      detach = attach(element)
    }

    const retarget = () => {
      if (disposed) return
      const next = resolveTarget()
      if (!next) return
      if (next === current && current?.isConnected) return
      follow(next)
    }

    /**
     * The anchor may not be mounted yet, for two very different reasons.
     *
     * Cheap one: a step asked the wizard to move first, and that render lands a
     * frame or two later. A fixed budget covers that.
     *
     * Expensive one: the step navigated somewhere that is still fetching, and
     * the page is showing a spinner where the anchor will be. That can take far
     * longer than any sensible fixed budget, and giving up on it produces the
     * "that part of the screen is not showing" message for something that was
     * simply still loading — telling the user to skip a step that was about to
     * work.
     *
     * So the idle budget is only spent while the app is idle. Time spent waiting
     * on the app's own queries does not count against it, up to a hard ceiling.
     */
    const startedAt = performance.now()
    let deadline = startedAt + IDLE_FIND_MS
    const hardDeadline = startedAt + MAX_FIND_MS

    const find = () => {
      if (disposed) return
      const element = resolveTarget()
      if (element) {
        follow(element)
        // Once found, keep watching for a dialog opening over the top. A slow
        // poll rather than a frame loop, because this can run for the minutes an
        // analysis takes.
        retargetTimer = window.setInterval(retarget, RETARGET_MS)
        return
      }
      const now = performance.now()
      // Still loading? Then this is not a missing anchor yet — push the budget
      // out, but never past the ceiling.
      if (isFetchingRef.current > 0) {
        deadline = Math.min(now + IDLE_FIND_MS, hardDeadline)
      }
      if (now > deadline) {
        setStatus('missing')
        return
      }
      findFrame = requestAnimationFrame(find)
    }
    find()

    return () => {
      disposed = true
      if (frame) cancelAnimationFrame(frame)
      if (findFrame) cancelAnimationFrame(findFrame)
      if (retargetTimer) window.clearInterval(retargetTimer)
      detach?.()
    }
  }, [anchor, focusAnchor])

  return { rect, status, missing: status === 'missing', onModal }
}
